import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import api, { type ApiError } from "../api/http";
import type {
  ApplyPresetRequest,
  ApplyPresetResult,
  ComparePresetsRequest,
  ComparePresetsResult,
  PresetCatalog,
  PresetDiff,
} from "../api/types";

/** GET the full preset catalog (business types + levels + payloads). */
export function usePresetCatalog() {
  return useQuery<PresetCatalog, ApiError>({
    queryKey: ["form-presets"],
    queryFn: () => api.get<PresetCatalog>("/api/v1/form-fields/presets/"),
    staleTime: 30 * 60 * 1000, // effectively static within a session
  });
}

/**
 * POST /presets/apply/ — applies the chosen (business_type, level) preset.
 * Invalidates every per-entity form-config + custom-fields-def query so the
 * detail pane re-fetches the new shape. Also invalidates the business/me
 * query so the rest of the app (header, settings page) picks up the new
 * business_type/preset_level.
 */
export function useApplyPreset() {
  const qc = useQueryClient();
  return useMutation<ApplyPresetResult, ApiError, ApplyPresetRequest>({
    mutationFn: (body) =>
      api.post<ApplyPresetResult>("/api/v1/form-fields/presets/apply/", body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["form-config"] });
      qc.invalidateQueries({ queryKey: ["form-field-defs"] });
      qc.invalidateQueries({ queryKey: ["business", "me"] });
      qc.invalidateQueries({ queryKey: ["businesses"] });
    },
  });
}

/** POST /presets/diff/ — preview the change that applying the preset would make. */
export function usePresetDiff() {
  return useMutation<PresetDiff, ApiError, ApplyPresetRequest>({
    mutationFn: (body) =>
      api.post<PresetDiff>("/api/v1/form-fields/presets/diff/", body),
  });
}

/** POST /presets/compare/ — N-way preset comparison for the comparison popup. */
export function useComparePresets() {
  return useMutation<ComparePresetsResult, ApiError, ComparePresetsRequest>({
    mutationFn: (body) =>
      api.post<ComparePresetsResult>("/api/v1/form-fields/presets/compare/", body),
  });
}
